import { listingHref } from "@shared/routing/listing-href";
import type { Id } from "@convex/_generated/dataModel";
import { formatCentsRaw } from "@/lib/formatting/currency";
import {
  formatConditionLabel,
  formatDimensionsLabel,
  type DimensionsLike,
} from "@shared/products/product-display";
import { formatListingLocation } from "@/lib/formatting/geography";
import { getProductDisplayNameFromUrlSlug } from "@/lib/products/catalog/helpers";

export interface ServerProductCardItem {
  _id: Id<"products">;
  title: string;
  buyerUnitPriceCents?: number | null;
  primaryImageUrl?: string | null;
  slug?: string | null;
  publicIdentifier?: string | null;
  cityName?: string | null;
  stateAbbr?: string | null;
  originZip?: string | null;
  checkoutEnabled?: boolean;
  intent?: "sell" | "recycle" | null;
  condition?: string | null;
  productTypeSlug?: string | null;
  quantityAvailable?: number | null;
  quantityReserved?: number | null;
  dimensions?: DimensionsLike | null;
  shippingMethods?: readonly string[] | null;
  productTypeName?: string | null;
  productType?: { displayName?: string | null } | null;
  distanceMiles?: number | null;
}

export type ProductCardDisplay = {
  href: string;
  name: string;
  dimensions: string | null;
  imageUrl: string | null;
  productTypeSlug: string | null;
  grade: string | null;
  unitPrice: string | null;
  freeLabel: string | null;
  quantityAvailable: number | null;
  location: string;
  pickupOnly: boolean;
  inStock: boolean;
  isFreePickup: boolean;
};

function derivePricing(item: ServerProductCardItem) {
  const buyerCents = item.buyerUnitPriceCents;
  const hasBuyerPrice = buyerCents !== undefined && buyerCents !== null;
  const unitCents = buyerCents ?? 0;
  const isFreePickup = item.intent === "recycle" && unitCents === 0;
  return {
    isFreePickup,
    isFree: isFreePickup || (hasBuyerPrice && buyerCents === 0),
    unitPrice: hasBuyerPrice && buyerCents > 0 ? formatCentsRaw(buyerCents) : null,
  };
}

function sellableQuantity(item: ServerProductCardItem): number | null {
  if (item.quantityAvailable === undefined || item.quantityAvailable === null) return null;
  return Math.max(item.quantityAvailable - (item.quantityReserved ?? 0), 0);
}

function deriveInStock(item: ServerProductCardItem, isFreePickup: boolean): boolean {
  if (item.checkoutEnabled === true) return true;
  if (!isFreePickup) return false;
  return (sellableQuantity(item) ?? 0) > 0;
}

function locationFor(item: ServerProductCardItem): string {
  const location = formatListingLocation(item.cityName, item.stateAbbr, item.originZip);
  if (typeof item.distanceMiles !== "number") return location;
  const distance = `${Math.round(item.distanceMiles).toLocaleString("en-US")} mi away`;
  return location ? `${location} · ${distance}` : distance;
}

function nameFor(item: ServerProductCardItem): string {
  const title = item.title?.trim();
  if (title) return title;
  const typeName =
    item.productType?.displayName?.trim() ||
    item.productTypeName?.trim() ||
    (item.productTypeSlug ? getProductDisplayNameFromUrlSlug(item.productTypeSlug) : null);
  return typeName ?? "Untitled listing";
}

function freeLabelFor(isFreePickup: boolean, isFree: boolean, freeCollect: boolean) {
  if (isFreePickup) return freeCollect ? "Free" : "Free pickup";
  return isFree ? "Free" : null;
}

export function productCardDisplay(
  item: ServerProductCardItem,
  { freeCollect = false, requestedQuantity }: { freeCollect?: boolean; requestedQuantity?: number } = {},
): ProductCardDisplay {
  const { isFreePickup, isFree, unitPrice } = derivePricing(item);
  const inStock = deriveInStock(item, isFreePickup);
  return {
    href: listingHref(item, requestedQuantity),
    name: nameFor(item),
    dimensions: formatDimensionsLabel(item.dimensions),
    imageUrl: item.primaryImageUrl?.trim() || null,
    productTypeSlug: item.productTypeSlug ?? null,
    grade: freeCollect && isFreePickup ? "Free" : formatConditionLabel(item.condition, item.productTypeSlug),
    unitPrice,
    freeLabel: freeLabelFor(isFreePickup, isFree, freeCollect),
    quantityAvailable: inStock ? sellableQuantity(item) : null,
    location: locationFor(item),
    pickupOnly: item.shippingMethods !== undefined && item.shippingMethods !== null && item.shippingMethods.length === 0,
    inStock,
    isFreePickup,
  };
}
